import OwButton from "@components/shared/OwButton/OwButton";
import { OwFormikField } from "@components/shared/OwFormikField/OwFormikField";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import IDuplicateCatalogItemDTO from "@models/DTO/IDuplicateCatalogItemDTO";
import { SocketContext } from "@contexts/SocketContext";
import UserContext from "@contexts/UserContext";
import ICatalog_ItemUI from "@models/ICatalog_ItemUI";
import { Formik } from "formik";
import { useContext, useEffect, useState } from "react";
import * as Yup from "yup";
import EndpointsEnum from "@models/EndpointsEnum";
import SupplierContext from "@contexts/SupplierContext";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import UiContext from "@contexts/UiContext";
import INotification from "@models/INotification";
import IOnDuplicateCatalogItemDTO from "@models/DTO/IOnDuplicateCatalogItemDTO";
import IUiSocketSetup from "@models/IUiSocketSetup";
import IErrorDTO from "@models/DTO/IErrorDTO";

type Props = {
    item: ICatalog_ItemUI;
    closeModal: (refresh?: boolean) => void;
};

export const CloneCatalogItemModal = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const schema = Yup.object().shape({
        sku: Yup.string().required(),
        name: Yup.string().required(),
        description: Yup.string().required(),
    });

    const [isLoading, setIsLoading] = useState(false);

    const onDuplicateCatalogItem = (data: IOnDuplicateCatalogItemDTO) => {
        const notification: INotification = {
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
            message: "successfully_cloned_catalog_item",
        };

        uiContext.notifyFn(null, notification);

        setIsLoading(false);

        props.closeModal(true);
    };

    const notify = (data: IErrorDTO) => {
        setIsLoading(false);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.CatalogItemOnDuplicateCatalogItem, fn: onDuplicateCatalogItem },
        { endpoint: EndpointsEnum.Notify, fn: notify },
    ];

    useEffect(() => {
        if (socket) {
            for (const _socket of sockets) {
                socket.on(_socket.endpoint, _socket.fn);
            }
        }

        return () => {
            if (socket) {
                for (const _socket of sockets) {
                    socket.off(_socket.endpoint, _socket.fn);
                }
            }
        };
    }, []);

    const duplicateCatalogItem = (row: ICatalog_ItemUI) => {
        const payload: IDuplicateCatalogItemDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            catalogItemGuid: row.guid,
            newCatalogItem: row,
        };

        socket.emit(EndpointsEnum.CatalogItemDuplicateCatalogItem, payload);

        setIsLoading(true);
    };

    return (
        <div className={"CloneCatalogItemModal p-2"}>
            <div className="modal-header d-flex justify-content-between">
                <OwTitle leftIconName="fad fa-clone" title="clone_catalog_item" tag="h5" />
                <div>
                    <OwButton
                        className="btn-danger"
                        centerIconClass="fad fa-times"
                        fn={() => {
                            props.closeModal();
                        }}
                    />
                </div>
            </div>
            <div className="modal-body">
                <div className="row">
                    <Formik
                        initialValues={{
                            sku: "",
                            name: "",
                            description: "",
                        }}
                        validationSchema={schema}
                        onSubmit={(values) => {
                            const newItem = {
                                ...props.item,
                                sku: values.sku,
                                name: values.name,
                                description: values.description,
                            };

                            duplicateCatalogItem(newItem);
                        }}>
                        {({ values, handleChange, handleBlur, handleSubmit, errors, touched, isValid, dirty }) => (
                            <form onSubmit={handleSubmit}>
                                <div className="col-12">
                                    <OwFormikField label="new_sku" name="sku" />
                                </div>
                                <div className="col-12">
                                    <OwFormikField label="new_name" name="name" />
                                </div>
                                <div className="col-12">
                                    <OwFormikField as="textarea" label="new_description" name="description" />
                                </div>
                                <div className="col-12">
                                    <OwButton
                                        type="submit"
                                        title="start"
                                        className="btn-primary"
                                        disabled={!isValid || !dirty}
                                        active={isLoading}
                                        disableOnActive={true}
                                    />
                                </div>
                            </form>
                        )}
                    </Formik>
                </div>
            </div>
        </div>
    );
};
